import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  Loader2,
  Sparkles,
  TrendingUp,
  ShoppingCart,
  Plus,
  Clock,
  ArrowRight,
  Store,
  Star,
  Check,
  ChefHat,
  Heart,
  Plane,
  Car,
  Brush,
  PartyPopper,
  ChevronDown,
  BookOpen,
} from "lucide-react";
import request from "../utils/request";
import {
  loadCatalogAndHost,
  updateService,
  bulkUpdateServices,
  createCustomService,
  selectCatalogList,
  selectServicesMap,
  selectCatalogStatus,
  selectHostStatus,
} from "../store/slices/servicesSlice";
import { getHostProperties } from "../store/thunks/hostActions";
import ServiceCard, { cardVariants } from "../components/services/ServiceCard";
import SubscriptionSummary from "../components/services/SubscriptionSummary";
import ServiceConfigDrawer from "../components/services/ServiceConfigDrawer";
import CreateServiceModal from "../components/services/CreateServiceModal";
import GuidebookPreview from "../components/services/GuidebookPreview";
import PhoneDemo from "../components/PhoneDemo";
import UNSPLASH from "../components/unsplashImages";
import {
  trackConciergeSubscribeClick,
  trackConciergeSubscribeError,
  trackConciergeManageBillingClick,
  trackConciergeCancelConfirmed,
  trackConciergeSubscriptionPageView,
  trackConciergeCheckoutReturn,
} from "../utils/hostAnalytics";

const PER_SERVICE_PRICE = 2;
const FREE_SERVICES = new Set(["grocery_delivery", "grocery-delivery"]);
const MONETIZABLE_KEYS = new Set(["early_checkin", "late_checkout"]);
const PHONE_SCALE = 210 / PhoneDemo.WIDTH;

const KEY_TO_DEMO_ID = {
  private_chef: "private-chef",
  massage: "massage",
  airport_ride: "airport-pickup",
  arrival_event: "arrival-event",
  wellness_therapy: "wellness-therapy",
  restaurant_reservations: "restaurant-reservations",
  other: "other",
};

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

const sectionFade = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function ConciergeSubscriptionScreen() {
  const dispatch = useDispatch();
  const { hostInfo } = useSelector((state) => state.host);
  const hostId = hostInfo?._id;

  const catalog = useSelector(selectCatalogList);
  const servicesMap = useSelector(selectServicesMap);
  const catalogStatus = useSelector(selectCatalogStatus);
  const hostServicesStatus = useSelector(selectHostStatus);
  const hostProps = useSelector((s) => s.hostProperties?.properties || []);
  const hostDetails = useSelector((s) => s.hostDetails?.Info);
  const hostLogo = hostDetails?.logoUrl || hostDetails?.profilePicture || null;

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const checkoutStatusParam = searchParams.get("status");
  const pageViewSent = useRef(false);
  const pricingRef = useRef(null);

  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerService, setDrawerService] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const [previewToggles, setPreviewToggles] = useState({});

  const enabledServicesForDemo = useMemo(() => {
    const set = new Set(["grocery-delivery"]);
    Object.entries(previewToggles).forEach(([key, on]) => {
      if (on) {
        const demoId = KEY_TO_DEMO_ID[key];
        if (demoId) set.add(demoId);
      }
    });
    return set;
  }, [previewToggles]);

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  useEffect(() => {
    if (!hostId) return;
    setStatusLoading(true);
    request
      .get(`/hosts/${hostId}/concierge/status`)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ conciergeEnabled: false, status: "none" }))
      .finally(() => setStatusLoading(false));
  }, [hostId]);

  useEffect(() => {
    if (hostId) {
      dispatch(loadCatalogAndHost(hostId));
      dispatch(getHostProperties(hostId));
    }
  }, [dispatch, hostId]);

  useEffect(() => {
    if (statusLoading || !hostId || !status || pageViewSent.current) return;
    pageViewSent.current = true;
    trackConciergeSubscriptionPageView({
      hostId,
      subscriptionStatus: status.conciergeEnabled ? "active" : "not_subscribed",
    });
  }, [statusLoading, hostId, status]);

  useEffect(() => {
    if (statusLoading || !hostId || !checkoutStatusParam) return;
    trackConciergeCheckoutReturn({ hostId, statusParam: checkoutStatusParam });
  }, [statusLoading, hostId, checkoutStatusParam]);

  const isSubscribed = !!status?.conciergeEnabled;
  const dataLoading =
    statusLoading || catalogStatus === "loading" || hostServicesStatus === "loading";

  const merged = useMemo(() => {
    const catalogKeys = new Set((catalog || []).map((d) => d.key));
    const fromCatalog = (catalog || []).map((def) => {
      const host = servicesMap[def.key] || {};
      return { ...def, hostService: host };
    });
    const hostOnly = Object.entries(servicesMap || {})
      .filter(([key]) => !catalogKeys.has(key) && key.startsWith("custom_"))
      .map(([key, svc]) => ({
        key,
        name: svc.name || key,
        description: svc.description || "",
        category: svc.category || "Custom",
        isCustom: true,
        hostService: svc,
      }));
    return [...fromCatalog, ...hostOnly];
  }, [catalog, servicesMap]);

  const freeServices = useMemo(
    () => merged.filter((s) => FREE_SERVICES.has(s.key)),
    [merged]
  );
  const paidServices = useMemo(
    () => merged.filter((s) => !FREE_SERVICES.has(s.key) && !s.isCustom),
    [merged]
  );
  const customServices = useMemo(
    () => merged.filter((s) => s.isCustom || s.key?.startsWith("custom_")),
    [merged]
  );
  const signatureServices = useMemo(
    () => paidServices.filter((s) => !MONETIZABLE_KEYS.has(s.key)),
    [paidServices]
  );
  const monetizableServices = useMemo(
    () => paidServices.filter((s) => MONETIZABLE_KEYS.has(s.key)),
    [paidServices]
  );

  const enabledPaidCount = useMemo(() => {
    if (!isSubscribed) {
      return Object.values(previewToggles).filter(Boolean).length;
    }
    return paidServices.filter((s) => s.hostService?.enabled).length;
  }, [isSubscribed, paidServices, previewToggles]);

  const homeCount = hostProps.length;

  const handleSubscribe = async (tier = "basic", placement = "unknown") => {
    const plan = status?.plans?.basic;
    const priceShownUsd = plan ? plan.priceInCents / 100 : 100;
    trackConciergeSubscribeClick({
      hostId, source: "service_hub", tier, placement, priceShownUsd,
    });
    setActionLoading(true);
    try {
      const { data } = await request.post(
        `/hosts/${hostId}/concierge/subscribe`,
        { tier }
      );
      if (data.url) window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to start checkout";
      trackConciergeSubscribeError({ hostId, source: "service_hub", message: msg });
      alert(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleManage = async () => {
    trackConciergeManageBillingClick({ hostId });
    setActionLoading(true);
    try {
      const { data } = await request.post(`/hosts/${hostId}/concierge/portal`);
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to open billing portal");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure? Your subscription stays active until the end of the billing period.")) return;
    trackConciergeCancelConfirmed({ hostId });
    setActionLoading(true);
    try {
      await request.post(`/hosts/${hostId}/concierge/cancel`);
      setStatus((prev) => ({ ...prev, cancelAtPeriodEnd: true }));
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to cancel");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggle = async (key, next) => {
    if (!isSubscribed) {
      setPreviewToggles((prev) => ({ ...prev, [key]: next }));
      return;
    }
    try {
      await dispatch(updateService({ hostId, key, patch: { enabled: next } })).unwrap?.();
    } catch {
      // optimistic rollback handled by slice
    }
  };

  const handleConfigure = (service) => {
    setDrawerService(service);
    setDrawerOpen(true);
  };

  const handlePricingChange = (key, nextPricing) => {
    dispatch(updateService({ hostId, key, patch: { pricing: nextPricing } }));
  };

  const handlePropertyScopeChange = (key, selectedIds, applyAll) => {
    const patch = applyAll ? { propertyIds: [] } : { propertyIds: selectedIds };
    dispatch(updateService({ hostId, key, patch }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    const updates = merged.map((m) => ({
      key: m.key,
      enabled: !!m.hostService?.enabled,
      visibility: "public",
      pricing: m.hostService?.pricing || {},
      propertyIds: m.hostService?.propertyIds || [],
    }));
    await dispatch(bulkUpdateServices({ hostId, updates }));
    setSaving(false);
  };

  const handleCreateService = async (serviceData) => {
    setCreateSubmitting(true);
    try {
      await dispatch(
        createCustomService({
          hostId,
          serviceData: {
            name: serviceData.name,
            description: serviceData.description,
            category: serviceData.category || "Custom",
            pricing: serviceData.pricing,
            enabled: serviceData.enabled,
            visibility: serviceData.visibility || "public",
          },
        })
      ).unwrap();
      setCreateModalOpen(false);
    } catch (err) {
      alert(typeof err === 'string' ? err : "Could not create service. Please try again.");
    } finally {
      setCreateSubmitting(false);
    }
  };

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  const statusParam = searchParams.get("status");

  return (
    <div className="min-h-screen pb-36 laptop:pb-16">
      {/* Success banner */}
      {statusParam === "success" && (
        <div className="max-w-7xl mx-auto px-4 laptop:px-6 pt-6">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-center gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            <p className="text-sm text-emerald-800 font-medium">
              Subscription activated! Your guests can now book concierge services.
            </p>
          </motion.div>
        </div>
      )}

      {/* 1. Hero Banner — non-subscribers only */}
      {!isSubscribed && (
        <div className="max-w-7xl mx-auto px-4 laptop:px-6 pt-6">
          <HeroBanner onSubscribe={scrollToPricing} actionLoading={actionLoading} enabledServices={enabledServicesForDemo} hostLogo={hostLogo} previewToggles={previewToggles} onToggle={handleToggle} />
        </div>
      )}

      {/* Subscribed header */}
      {isSubscribed && (
        <div className="max-w-7xl mx-auto px-4 laptop:px-6 pt-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl laptop:text-3xl font-extrabold text-gray-900" style={{ letterSpacing: -0.5 }}>
                Concierge
              </h1>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold ring-1 ring-emerald-100">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                Subscribed
              </span>
            </div>
            <p className="text-sm text-gray-500">
              Configure services, set pricing, and manage what your guests can book.
            </p>
          </motion.div>
        </div>
      )}

      {/* 2. Proof Strip */}
      <ProofStrip />

      {/* 3. Guest Experience + Guidebook Preview */}
      <motion.section
        variants={sectionFade}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-50px" }}
        className="max-w-7xl mx-auto px-4 laptop:px-6 mt-10"
      >
        <SectionHeader
          icon={<BookOpen size={16} />}
          label="Free Guidebook For Every Stay"
          sublabel="Preview what your guests will see — a branded guidebook with local favorites, property details, and concierge access"
        />
        <div className="mt-5">
          <GuidebookPreview hostLogo={hostLogo} customServices={customServices} />
        </div>
      </motion.section>

      {/* 4. Included Free */}
      <motion.section variants={sectionFade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }} className="max-w-7xl mx-auto px-4 laptop:px-6 mt-12">
        <SectionHeader
          icon={<ShoppingCart size={16} />}
          label="Included with Bellhopt"
          sublabel="Active on all your properties — no subscription needed"
          accent="emerald"
        />
        <motion.div variants={staggerContainer} initial="hidden" animate="show" className="mt-4">
          {freeServices.map((svc) => (
            <ServiceCard
              key={svc.key}
              service={svc}
              hostService={svc.hostService}
              isSubscribed={isSubscribed}
              isFree={true}
              perServicePrice={0}
              onToggle={handleToggle}
              onConfigure={handleConfigure}
            />
          ))}
          {freeServices.length === 0 && (
            <ServiceCard
              service={{ key: "grocery_delivery", name: "Pre-Arrival Grocery Delivery", category: "Provisions" }}
              hostService={{ enabled: true }}
              isSubscribed={isSubscribed}
              isFree={true}
              perServicePrice={0}
              onToggle={handleToggle}
              onConfigure={handleConfigure}
            />
          )}
        </motion.div>
      </motion.section>

      {/* 5. Signature Concierge */}
      <motion.section variants={sectionFade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }} className="max-w-7xl mx-auto px-4 laptop:px-6 mt-12">
        <SectionHeader
          icon={<Sparkles size={16} />}
          label={isSubscribed ? "Signature Concierge" : "Unlock Signature Concierge Services"}
          sublabel={
            isSubscribed
              ? "Toggle services on or off, configure pricing and availability"
              : "Premium guest services — toggle to preview, subscribe to go live"
          }
        />
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 laptop:grid-cols-2 gap-4 mt-4"
        >
          {signatureServices.map((svc) => (
            <ServiceCard
              key={svc.key}
              service={svc}
              hostService={
                isSubscribed
                  ? svc.hostService
                  : { enabled: !!previewToggles[svc.key] }
              }
              isSubscribed={isSubscribed}
              isFree={false}
              perServicePrice={PER_SERVICE_PRICE}
              onToggle={handleToggle}
              onConfigure={handleConfigure}
            />
          ))}
        </motion.div>
      </motion.section>

      {/* 6. Monetizable Upgrades */}
      {monetizableServices.length > 0 && (
        <motion.section variants={sectionFade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }} className="max-w-7xl mx-auto px-4 laptop:px-6 mt-12">
          <SectionHeader
            icon={<Clock size={16} />}
            label="Monetizable Upgrades"
            sublabel="Earn revenue on flexible arrival and departure — guests love it"
          />
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 laptop:grid-cols-2 gap-4 mt-4"
          >
            {monetizableServices.map((svc) => (
              <ServiceCard
                key={svc.key}
                service={svc}
                hostService={
                  isSubscribed
                    ? svc.hostService
                    : { enabled: !!previewToggles[svc.key] }
                }
                isSubscribed={isSubscribed}
                isFree={false}
                perServicePrice={PER_SERVICE_PRICE}
                onToggle={handleToggle}
                onConfigure={handleConfigure}
              />
            ))}
          </motion.div>
        </motion.section>
      )}

      {/* 7. Create Your Own Services */}
      <motion.section variants={sectionFade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }} className="max-w-7xl mx-auto px-4 laptop:px-6 mt-12">
        <SectionHeader
          icon={<Store size={16} />}
          label="Create Your Own Services"
          sublabel="Build a guest marketplace tailored to your properties"
        />
        <motion.div variants={staggerContainer} initial="hidden" animate="show" className="grid grid-cols-2 laptop:grid-cols-3 gap-4 mt-4">
          {customServices.map((svc) => {
            const locked = !isSubscribed;
            return (
              <motion.div
                key={svc.key}
                variants={cardVariants}
                whileHover={locked ? {} : { y: -4 }}
                className={`relative overflow-hidden rounded-2xl transition-all duration-200 cursor-pointer group ${
                  locked
                    ? "border border-gray-200 opacity-80"
                    : "border border-gray-200 hover:shadow-md"
                }`}
                onClick={() => !locked && handleConfigure(svc)}
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={UNSPLASH.services.customRequest}
                    alt={svc.name}
                    className={`w-full h-full object-cover transition-all duration-300 ${
                      locked ? "saturate-0 opacity-60" : "group-hover:scale-105"
                    }`}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                  {locked && (
                    <div className="absolute top-2.5 right-2.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-800/70 text-white text-[10px] font-bold backdrop-blur-sm">
                        Unlock with Concierge
                      </span>
                    </div>
                  )}

                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <h4 className={`text-sm font-bold leading-snug ${locked ? "text-gray-300" : "text-white"}`}>
                      {svc.name}
                    </h4>
                    {svc.hostService?.pricing?.flatUSD != null && (
                      <p className="text-xs text-white/70 mt-0.5">${svc.hostService.pricing.flatUSD}</p>
                    )}
                    {locked && (
                      <p className="text-[10px] text-white/50 mt-1">Draft — not yet live</p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Create new */}
          <motion.div
            variants={cardVariants}
            whileHover={{ y: -4, borderColor: "rgba(239,73,73,0.3)" }}
            className="relative overflow-hidden rounded-2xl border-2 border-dashed border-gray-200 transition-all duration-200 cursor-pointer flex items-center justify-center aspect-square bg-white"
            onClick={() => setCreateModalOpen(true)}
          >
            <div className="text-center px-4">
              <div className="w-12 h-12 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-3">
                <Plus size={20} className="text-primary-500" />
              </div>
              <h4 className="text-sm font-bold text-gray-900 mb-1">
                Create a service
              </h4>
              <p className="text-xs text-gray-400 leading-relaxed">
                Rentals, experiences, add-ons — anything your guests can book.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* 8. Subscription Summary / Close */}
      <div ref={pricingRef} className="mt-14 border-t border-gray-100 bg-gradient-to-b from-gray-50/80 to-white">
        <div className="max-w-xl mx-auto px-4 laptop:px-6 py-10">
          <SubscriptionSummary
            isSubscribed={isSubscribed}
            status={status}
            enabledPaidCount={enabledPaidCount}
            totalPaidCount={paidServices.length}
            perServicePrice={PER_SERVICE_PRICE}
            homeCount={homeCount}
            onSubscribe={handleSubscribe}
            onManage={handleManage}
            onCancel={handleCancel}
            actionLoading={actionLoading}
            paidServices={paidServices}
            previewToggles={previewToggles}
            onToggle={handleToggle}
          />
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      <MobileBottomBar
        isSubscribed={isSubscribed}
        enabledPaidCount={enabledPaidCount}
        perServicePrice={PER_SERVICE_PRICE}
        homeCount={homeCount}
        status={status}
        onSubscribe={isSubscribed ? handleManage : scrollToPricing}
        onManage={handleManage}
        actionLoading={actionLoading}
      />

      {/* Drawer */}
      <ServiceConfigDrawer
        open={drawerOpen}
        service={drawerService}
        hostService={drawerService ? servicesMap[drawerService.key] : null}
        properties={hostProps}
        onClose={() => setDrawerOpen(false)}
        onToggle={handleToggle}
        onPricingChange={handlePricingChange}
        onPropertyScopeChange={handlePropertyScopeChange}
        onSave={handleSaveAll}
        saving={saving}
      />

      {/* Create service modal */}
      <CreateServiceModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateService}
        submitting={createSubmitting}
      />
    </div>
  );
}

/* ─── All concierge services for hero toggles ─── */
const HERO_SERVICES = [
  { key: "private_chef", icon: ChefHat, displayName: "Private Chef" },
  { key: "massage", icon: Heart, displayName: "In-Home Massage" },
  { key: "airport_ride", icon: Plane, displayName: "Airport Transfer" },
  { key: "early_checkin", icon: Clock, displayName: "Early Check-In" },
  { key: "late_checkout", icon: Clock, displayName: "Late Check-Out" },
  { key: "mid_stay_cleaning", icon: Brush, displayName: "Mid-Stay Cleaning" },
  { key: "arrival_event", icon: PartyPopper, displayName: "Event Setup" },
  { key: "car_rental", icon: Car, displayName: "Car Rentals" },
  { key: "other", icon: Sparkles, displayName: "Custom Requests" },
];

const HERO_INITIAL_COUNT = 5;

/* ─── Hero Banner ─── */
function HeroBanner({ onSubscribe, actionLoading, enabledServices, hostLogo, previewToggles, onToggle }) {
  const enabledCount = Object.values(previewToggles || {}).filter(Boolean).length;
  const [expanded, setExpanded] = useState(false);
  const visibleServices = expanded ? HERO_SERVICES : HERO_SERVICES.slice(0, HERO_INITIAL_COUNT);
  const hiddenCount = HERO_SERVICES.length - HERO_INITIAL_COUNT;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative overflow-hidden rounded-3xl"
      style={{
        background: `linear-gradient(135deg, rgba(255,255,255,0.97) 55%, rgba(254,242,242,0.92) 100%), url(${UNSPLASH.hero})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        border: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      <div className="flex flex-col laptop:flex-row items-stretch">
        {/* Left: Copy + free/premium toggle sections */}
        <div className="flex-1 p-6 laptop:p-10 laptop:pr-6">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary-500 mb-4">
            Bellhopt Concierge
          </p>

          <h1
            className="text-2xl laptop:text-[34px] font-extrabold text-gray-900 mb-3"
            style={{ lineHeight: 1.08, letterSpacing: -0.5 }}
          >
            Give your guests a<br />
            5-star experience.
            <br />
            <span className="text-primary-500">Earn more from every stay.</span>
          </h1>

          <p className="text-sm text-gray-500 leading-relaxed mb-5 max-w-md">
            Grocery delivery is already free. Subscribe to unlock concierge
            services, or create your own and earn from every booking.
          </p>

          {/* Free included */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                <ShoppingCart size={13} className="text-emerald-600" strokeWidth={2} />
              </div>
              <span className="text-[13px] font-semibold text-gray-900 flex-1">Pre-Arrival Grocery Delivery</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold uppercase">
                <Check size={8} strokeWidth={3} />
                Free
              </span>
            </div>
          </div>

          {/* Premium services with real toggles */}
          <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-2">
            Premium concierge services — toggle to preview
          </p>
          <div className="space-y-1 mb-2">
            {visibleServices.map((svc) => {
              const on = !!previewToggles?.[svc.key];
              return (
                <div
                  key={svc.key}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all ${
                    on
                      ? "bg-white border-primary-200 shadow-sm"
                      : "bg-white/60 border-gray-100 hover:border-gray-200"
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                      on ? "bg-primary-500 text-white" : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <svc.icon size={13} strokeWidth={2} />
                  </div>
                  <span className={`flex-1 text-[13px] font-medium ${on ? "text-gray-900" : "text-gray-500"}`}>
                    {svc.displayName}
                  </span>
                  <button
                    onClick={() => onToggle(svc.key, !on)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      on ? "bg-primary-500" : "bg-gray-300"
                    }`}
                  >
                    <motion.span
                      className="inline-block h-4 w-4 rounded-full bg-white shadow-sm"
                      animate={{ x: on ? 17 : 2 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </button>
                </div>
              );
            })}
          </div>
          {hiddenCount > 0 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1.5 text-[12px] font-semibold text-primary-500 hover:text-primary-600 transition-colors mb-4"
            >
              {expanded ? "Show less" : `Show all ${HERO_SERVICES.length} services`}
              <motion.span
                animate={{ rotate: expanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="inline-flex"
              >
                <ChevronDown size={14} />
              </motion.span>
            </button>
          )}

          <div className="flex items-center gap-4">
            <button
              onClick={() => onSubscribe()}
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary-500 text-white text-sm font-bold rounded-xl hover:bg-primary-600 transition-colors shadow-bellhopt"
            >
              {enabledCount > 0 ? `Get Started — ${enabledCount} selected` : "Get Started"}
              <ArrowRight size={15} strokeWidth={2.5} />
            </button>
            <span className="text-xs text-gray-400 flex items-center gap-1.5">
              <Check size={12} className="text-emerald-500" />
              Cancel anytime
            </span>
          </div>
        </div>

        {/* Right: Phone preview */}
        <div className="hidden laptop:flex items-center justify-center px-6 py-8 flex-shrink-0">
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            style={{
              width: 220,
              height: 448,
              borderRadius: 38,
              background: "#000",
              border: "1px solid rgba(0,0,0,0.1)",
              position: "relative",
              boxShadow: "0 30px 80px rgba(0,0,0,0.15), 0 10px 30px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            <div style={{
              position: "absolute", top: 7, left: "50%", transform: "translateX(-50%)",
              width: 68, height: 20, borderRadius: 10, background: "#000", zIndex: 20,
            }} />
            <div style={{
              position: "absolute", top: 4, left: 4, right: 4, bottom: 4,
              borderRadius: 34, overflow: "hidden", background: "#fff",
            }}>
              <div style={{
                width: PhoneDemo.WIDTH, height: PhoneDemo.HEIGHT,
                transform: `scale(${PHONE_SCALE})`, transformOrigin: "top left",
              }}>
                <PhoneDemo enabledServices={enabledServices} hostLogo={hostLogo} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Proof Strip ─── */
function ProofStrip() {
  const pills = [
    { icon: <ShoppingCart size={14} />, text: "Free grocery included" },
    { icon: <Sparkles size={14} />, text: "Brandable guest app" },
    { icon: <Star size={14} />, text: "Premium services" },
    { icon: <TrendingUp size={14} />, text: "Earn on upgrades" },
  ];
  return (
    <div className="w-full border-y border-gray-100 bg-gray-50/60 mt-6">
      <div className="max-w-7xl mx-auto px-4 laptop:px-6 py-4">
        <div className="flex flex-wrap justify-center laptop:justify-between gap-3 laptop:gap-6">
          {pills.map((p) => (
            <div key={p.text} className="flex items-center gap-2 text-sm text-gray-600 font-medium">
              <span className="text-primary-500">{p.icon}</span>
              {p.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Section Header ─── */
function SectionHeader({ icon, label, sublabel, accent }) {
  const bg = accent === "emerald" ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-600";
  return (
    <div className="flex items-start gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${bg}`}>
        {icon}
      </div>
      <div>
        <h2 className="text-lg font-bold text-gray-900">{label}</h2>
        {sublabel && <p className="text-sm text-gray-400 mt-0.5">{sublabel}</p>}
      </div>
    </div>
  );
}

/* ─── Mobile Bottom Bar ─── */
function MobileBottomBar({
  isSubscribed, enabledPaidCount, perServicePrice, homeCount, status, onSubscribe, onManage, actionLoading,
}) {
  const monthlyTotal = enabledPaidCount * perServicePrice * Math.max(homeCount, 1);
  const plan = status?.plans?.basic;
  const priceLabel = plan ? `$${(plan.priceInCents / 100).toFixed(0)}` : `$${monthlyTotal || 0}`;

  return (
    <div className="fixed bottom-0 left-0 right-0 laptop:hidden bg-white/95 backdrop-blur-sm border-t border-gray-200 px-4 py-3 z-30">
      <div className="flex items-center justify-between max-w-lg mx-auto">
        <div>
          {isSubscribed ? (
            <>
              <p className="text-xs text-gray-500">Active subscription</p>
              <p className="text-lg font-extrabold text-gray-900">{priceLabel}/mo</p>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-500">
                {enabledPaidCount} service{enabledPaidCount !== 1 ? "s" : ""} selected
              </p>
              <AnimatePresence mode="wait">
                <motion.p
                  key={monthlyTotal}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-lg font-extrabold text-gray-900"
                >
                  ${monthlyTotal}/mo
                </motion.p>
              </AnimatePresence>
            </>
          )}
        </div>
        <button
          onClick={() => isSubscribed ? onManage() : onSubscribe("basic", "mobile_bar")}
          disabled={actionLoading}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-bold rounded-xl hover:bg-primary-600 transition-colors shadow-bellhopt disabled:opacity-60"
        >
          {actionLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isSubscribed ? (
            "Manage Billing"
          ) : (
            <>
              Get Started
              <ArrowRight size={14} strokeWidth={2.5} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
